import React, { useState, useEffect, useRef } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';

interface ArticleFeedbackProps {
  articleId: string;
  articleTitle: string;
}

// Generate or retrieve session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('neupath-session-id');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('neupath-session-id', sessionId);
  }
  return sessionId;
};

// Detect device type based on screen width
const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

const ArticleFeedback: React.FC<ArticleFeedbackProps> = ({ articleId, articleTitle }) => {
  const [hasVoted, setHasVoted] = useState(false);
  const [votedValue, setVotedValue] = useState<'up' | 'down' | null>(null);

  // Track engagement metrics
  const pageLoadTime = useRef(Date.now());
  const maxScrollDepth = useRef(0);

  // Check if user already voted on this article
  useEffect(() => {
    const storedVote = localStorage.getItem(`article-feedback-${articleId}`);
    if (storedVote) {
      setHasVoted(true);
      setVotedValue(storedVote as 'up' | 'down');
    }
  }, [articleId]);

  // Track scroll depth
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
      maxScrollDepth.current = Math.max(maxScrollDepth.current, scrollPercent);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const submitFeedback = (rating: 'up' | 'down') => {
    if (hasVoted) return;

    // Optimistic UI: immediately show success
    localStorage.setItem(`article-feedback-${articleId}`, rating);
    setHasVoted(true);
    setVotedValue(rating);

    // Calculate time on page in seconds
    const timeOnPage = Math.round((Date.now() - pageLoadTime.current) / 1000);

    // Fire and forget - submit in background, never show errors to user
    const firebase = (window as { firebase?: { functions: () => { httpsCallable: (name: string) => (data: Record<string, unknown>) => Promise<unknown> } } }).firebase;
    if (!firebase) return;

    const submitArticleFeedback = firebase.functions().httpsCallable('submitArticleFeedback');
    submitArticleFeedback({
      // Core feedback
      articleId,
      articleTitle,
      rating,

      // Source tracking
      pageUrl: window.location.href,
      referrer: document.referrer || 'direct',

      // User context
      userAgent: navigator.userAgent,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      deviceType: getDeviceType(),

      // Engagement metrics
      timeOnPage,
      scrollDepth: maxScrollDepth.current,

      // Session tracking
      sessionId: getSessionId()
    }).catch(() => {
      // Silently ignore errors - monitor via Cloud Function logs
    });
  };

  if (hasVoted) {
    return (
      <div className="flex items-center justify-center gap-3 py-6 px-4 bg-gray-50 rounded-xl">
        <span className="text-gray-600">Thanks for your feedback!</span>
        {votedValue === 'up' ? (
          <ThumbsUp className="w-5 h-5 text-teal-600 fill-teal-600" />
        ) : (
          <ThumbsDown className="w-5 h-5 text-gray-500 fill-gray-500" />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 py-6 px-4 bg-gray-50 rounded-xl">
      <span className="text-gray-700 font-medium">Was this article helpful?</span>
      <div className="flex gap-3">
        <button
          onClick={() => submitFeedback('up')}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition group"
          aria-label="Yes, this was helpful"
        >
          <ThumbsUp className="w-5 h-5 text-gray-400 group-hover:text-teal-600 transition" />
          <span className="text-gray-600 group-hover:text-teal-700 transition">Yes</span>
        </button>
        <button
          onClick={() => submitFeedback('down')}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:border-gray-400 hover:bg-gray-100 transition group"
          aria-label="No, this was not helpful"
        >
          <ThumbsDown className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition" />
          <span className="text-gray-600 group-hover:text-gray-700 transition">No</span>
        </button>
      </div>
    </div>
  );
};

export default ArticleFeedback;
